import { isHotkey } from 'is-hotkey'
import { useCallback, useEffect, useState } from 'react'

type Props = {
  count: number
  isActive: boolean
}

export const usePreviewIndex = ({ count, isActive }: Props) => {
  const [previewIndex, setPreviewIndex] = useState(0)

  const handleMoveToIndex = useCallback(
    (toIndex: number) => {
      if (count === 0) {
        return
      }

      let nextIndex = toIndex

      if (nextIndex < 0) {
        nextIndex = count + nextIndex
      }

      setPreviewIndex(nextIndex % count)
    },
    [count],
  )

  useEffect(
    function togglePreviewIndex() {
      if (!isActive || !count) {
        return
      }

      const listenToLeftRightArrowKeys = (event: KeyboardEvent) => {
        if (isHotkey(['mod+left', 'mod+right'], event)) {
          event.preventDefault()
          event.stopPropagation()

          handleMoveToIndex(
            isHotkey('mod+left', event) ? previewIndex - 1 : previewIndex + 1,
          )
        }
      }

      document.addEventListener('keydown', listenToLeftRightArrowKeys)

      return () => {
        document.removeEventListener('keydown', listenToLeftRightArrowKeys)
      }
    },
    [handleMoveToIndex, isActive, count, previewIndex],
  )

  const next = useCallback(
    () => handleMoveToIndex(previewIndex + 1),
    [handleMoveToIndex, previewIndex],
  )
  const previous = useCallback(
    () => handleMoveToIndex(previewIndex - 1),
    [handleMoveToIndex, previewIndex],
  )

  const reset = useCallback(() => setPreviewIndex(0), [])

  return { index: previewIndex, next, previous, reset }
}
